import type { RotateTuning, ScalePulseTuning, ShineSweepTuning, SymbolWinBrightnessTuning, WinAmountLabelSpineExplosionTuning, WinHighlightTuning } from "@slot/presentation";

/**
 * Game tuning VALUES for the win presentation: the win HIGHLIGHT (dim non-winners
 * + floating amount label) and the winning-symbol animation (scale-pulse). Shape
 * interfaces are `@slot/presentation`'s:
 *   - `WinHighlightTuning` — `effects/win-highlight/`
 *   - `WinAmountLabelSpineExplosionTuning` — `effects/win-amount-label/`
 *   - `ScalePulseTuning`        — `effects/symbol-win/`
 *
 * This is a tumbling-reels game: a win is highlighted ONCE, then the winning symbols
 * are removed and the grid tumbles — so there is no win re-cycle loop and no drawn
 * payline. The `.autoAdvance` block is the only game-local timing here.
 *
 * Consumers (all wired in createPresentation):
 *   - WinHighlightEffect           (`.layer`, its `timings`; `.layer.labelCollect` flies the label
 *     into the win meter)
 *   - WinAmountLabelSpineExplosion (`.label`, through WinHighlightEffect's `labelTuning`)
 *   - ScalePulseSymbolWinEffect    (`.scalePulse`; in the sprite winner composite, the fallback
 *     when there is no Spine `win` — SYMBOL_SPINE_WIN_ENABLED off, or no symbol skeletons)
 *
 * Edit values here to retune. Do not reintroduce parallel constants in any consumer.
 */

// Symbols that get the extra win ROTATION (RotateSymbolWinEffect) on top of the
// pulse + shine — the L (low-pay) tier. Explicit (not "every non-H symbol") so the
// wild (W) / scatter (FS) never wobble if one lands on a winning line. The H symbols
// switch frames instead (see symbolWinFrames.ts); they don't rotate.
export const SYMBOL_WIN_ROTATE_SYMBOL_IDS: readonly string[] = ["L1", "L2", "L3", "L4", "L5"];

// ---------------------------------------------------------------------------
// Composite tuning constant
// ---------------------------------------------------------------------------

export interface WinPresentationTuning {
  layer: WinHighlightTuning;
  label: WinAmountLabelSpineExplosionTuning;
  scalePulse: ScalePulseTuning;
  scalePulseL: ScalePulseTuning;
  rotate: RotateTuning;
  brightness: SymbolWinBrightnessTuning;
  shineSweep: ShineSweepTuning;
  symbolFrames: { fadeInSeconds: number; startDelayMilliseconds: number; baseFadeOutDelaySeconds: number };
  autoAdvance: { postWinHoldSeconds: number; postRoundHoldSeconds: number };
  winHighlightDelay: { firstSeconds: number; subsequentSeconds: number };
}

export const WIN_PRESENTATION_TUNING: WinPresentationTuning = {
  layer: {
    cycleDurationMilliseconds: 400,

    // Lead-in before the dim starts (0 = immediately at highlight time).
    dimStartDelayMilliseconds: 0,
    dimFadeSeconds: 0.05,
    // Darkens the symbol COLOUR (opaque) via negative exposure; more negative = darker (-0.5 ≈ ×0.5).
    dimExposure: -0.6,
    dimFadeEasing: "quadOut",
    // Only NON-winners dim; the winners stay LIT so their flourish reads on a bright symbol.
    // MUST stay false: a dimmed winner is revealed only by an effect that fires onShineStart, and
    // none of this game's winner effects (the Spine `win`, the sprite winner composite) fires it, so
    // dimWinners:true would leave the winners dimmed. See WinHighlightTuning.dimWinners.
    dimWinners: false,

    // Faded un-dim timings (WinHighlightTuning shape). The spin-start clear
    // snaps the dim instantly regardless of these — so a fast slam re-spin never carries
    // still-dimmed cells onto the new winners; here the winners just tumble away.
    // The removal waits for this un-dim, and the meter update (the label's flight, below) waits for
    // the removal: 250 ms brings the update to the end of the cannon's burst on the label, so the
    // win flies to the meter straight from the burst (500 left the label idle ~0.25 s first).
    dimEndStartDelayMilliseconds: 250,
    dimEndFadeSeconds: 0.2,

    labelInStartDelayMilliseconds: 100,
    labelOutStartDelayMilliseconds: 1100,
    // How long a merged label holds the multiplied total before it hides — unused here: the label
    // flies into the meter instead (labelCollect).
    labelMergeLingerMilliseconds: 500,

    // The tumble's win FLIES into the win meter (WinHighlightTuning.labelCollect): after the cannon's
    // merge the label holds through the removal; the meter update lifts its digits off — a swell, then
    // a quick flight up on a slight arc (bowing to the right, arcHeight design units at its middle),
    // shrinking to the meter's digit size — while its plate fades where it stands. The meter takes the
    // new total as the digits touch it, and they sink into it.
    labelCollect: {
      popScale: 1.15,
      popSeconds: 0.08,
      flySeconds: 0.25,
      flyEasing: "quadIn",
      arcHeight: 60,
    },

    // Anchor the win-amount label at the GRID CENTRE rather than on the winning cluster. This is a
    // tumble game: wins land all over the grid and a substep can hold several clusters at once, so a
    // fixed centre reads far steadier than a label hopping per win (and it's where the cannon's
    // fired multiplier merges into the amount). The presentation default is "clusterCenter" — see
    // WinHighlightTuning.labelPlacement.
    labelPlacement: "gridCenter",

    // Multiple wins in ONE substep (several grid_win_highlight actions — common here, wins land all
    // over) show as ONE label with the running TOTAL, not one label per win: it accumulates the base
    // total as each win lands (bumping up), then POP-REPLACES to the multiplied total on the cannon
    // fire — no "base xN" text (the cannon already shows the multiplier). See WinHighlightTuning.labelMode.
    labelMode: "substepTotal",

    // Per-symbol sweep: each winning cell's animation starts this many ms after the previous
    // one (left→right). 0 = all winners animate TOGETHER — what this game wants: every winner
    // fires its Spine `win` on the same frame, so the beat is one clean flash rather than a
    // walk across the cluster (and its length is just the clip, independent of cluster size).
    symbolAnimStaggerMilliseconds: 0,

    // Hold the beat until every winner's Spine `win` has finished, instead of the fixed 400ms
    // above (which stays the floor) — otherwise the removal action, which fires right after,
    // truncates the flourish (at 400ms vs a 667ms clip only 60% played). The layer measures
    // max(stagger × k + clipLength) from the win effect, so with the stagger at 0 this is simply
    // the longest clip among the winners — 0.667s, or 1.0s when a skull/CH/FS is in the cluster.
    awaitSymbolWinAnimations: true,
  },

  label: {
    hideSeconds: 0.3,
    // The number-explosion Spine drives the win-amount digits (socketed to `number_placeholder`):
    // `in` pops them in on appear; `explosion` bursts them to the multiplied total on the cannon fire
    // (only on a multiplied substep — the layer gates it on `hasMultiplier`). Clip timings are in the asset.
    inClip: "in",
    explosionClip: "explosion",
    // Skip the explosion clip's ~0.1s burst fade-in so it's visible AT impact (no dead gap after the
    // bullet lands + vanishes). 0 = play the full fade-in.
    explosionSkipSeconds: 0.1,
    placeholder: "number_placeholder",
    // Uniform scale for the socketed digits (the socket drives a follower; digits are a scaled child).
    digitScale: 1,
  },

  // Winning-symbol animation — the scale-pulse (ScalePulseSymbolWinEffect): a Y-only
  // squash-and-stretch (squeeze → pop → settle); X is held at base the whole time. It plays in the
  // sprite winner composite (no Spine `win`, see the header); the Spine `win` clip replaces it otherwise.
  scalePulse: {
    scaleInStartDelayMilliseconds: 80,
    // Squeeze (squash) Y down before the pop. squeezeFactor: 1 skips the squeeze.
    squeezeFactor: 1,
    squeezeSeconds: 0.08,
    squeezeEasing: "quadOut",
    // Stretch ("pop") Y up, HOLD at the big state, then settle back to base. X stays as-is.
    scaleFactor: 1.2,
    scaleUpSeconds: 0.1,
    scaleUpEasing: "quadOut",
    scaleHoldSeconds: 0, // dwell at the stretched peak before settling (0 = settle immediately)
    scaleDownSeconds: 0.1,
    scaleDownEasing: "quadIn",
  },

  // L (low-pay) symbols' scale-pulse — distinct from the default pulse above (which covers H
  // and every non-L winner). Same shape (ScalePulseTuning); tune the L tier independently (e.g.
  // a harder squash/pop). Wired in createPresentation via a per-symbol tuning resolver:
  // L ids (SYMBOL_WIN_ROTATE_SYMBOL_IDS) → this, everything else → scalePulse. Starts as a copy.
  scalePulseL: {
    uniformScale: true, // L: pop on BOTH axes (X tracks Y), not the default Y-only squash-and-stretch
    scaleInStartDelayMilliseconds: 80,
    squeezeFactor: 1,
    squeezeSeconds: 0.08,
    squeezeEasing: "quadOut",
    scaleFactor: 1.2,
    scaleUpSeconds: 0.08,
    scaleUpEasing: "quadOut",
    scaleHoldSeconds: 0.05,
    scaleDownSeconds: 0.08,
    scaleDownEasing: "quadIn",
  },

  // Rotation ("wobble") symbol-win animation (RotateSymbolWinEffect) — runs ONLY on the
  // L symbols (SYMBOL_WIN_ROTATE_SYMBOL_IDS), on top of the pulse + shine. Drives node.angle
  // (independent of scale), so the L cell rotates as it pops and unrotates as it settles.
  // Timings mirror scalePulse so the two move together; tune independently to taste.
  // This game runs it ONCE per win (createPresentation passes once: true), like the pulse.
  rotate: {
    degrees: 0, // peak angle at the apex; sign sets direction (+ = CCW). 0 = no rotation.
    startDelayMilliseconds: 80, // = scalePulse.scaleInStartDelayMilliseconds
    rotateUpSeconds: 0.1, // = scalePulse.scaleUpSeconds (rotate out as it pops)
    rotateUpEasing: "quadOut",
    holdSeconds: 0, // = scalePulse.scaleHoldSeconds (hold the wobble while the scale holds)
    rotateDownSeconds: 0.1, // = scalePulse.scaleDownSeconds (rotate back as it settles)
    rotateDownEasing: "quadIn",
  },

  // Declared, not wired: no effect implements SymbolWinBrightnessTuning (the winner shine is the shine-sweep
  // below, the dim is AlphaDimEffect via WinHighlightTuning). Kept as the placeholder for a brightness win
  // effect; every value in this block is inert.
  brightness: {
    peak: 2, // INERT (winner shine is the shine-sweep); apex exposure gain if the shine were used
    startDelayMilliseconds: 80,
    rampUpSeconds: 0.05, // 0 = snap to peak
    rampUpEasing: "quadOut",
    holdSeconds: 0.0, // hold at peak before fading back
    rampDownSeconds: 0.02,
    rampDownEasing: "quadIn",
  },

  // Shine-SWEEP symbol-win animation (ShineSweepSymbolWinEffect) — the winner's SHINE in the sprite
  // winner composite (the brightness block above is not wired). A one-shot tilted "glint" band travels
  // across each winning symbol once as it scales up: a per-sprite ShineSweepFilter whose `progress`
  // the effect ramps 0→1 on engine time. It touches ONLY the sprite's filters, so it stacks on the
  // scale-pulse (node.scale) + rotate (node.angle). Runs ONCE per win (once: true). The cannon's
  // reload glint sweeps with it too, on every reload.
  // Shape (peak/tilt/width/softness) = the filter's defaults, the values of the reels template's
  // material it ports; sweepSeconds ≈ one sweep at that material's authored speed.
  shineSweep: {
    peak: 1.99, // = the filter's default `highlight`; band-centre exposure gain (rgb *= 1 + peak·band)
    tilt: -0.5, // band lean across the sprite (0 = vertical); − leans it like the filter's default
    width: 0.1, // band half-width, frame-local 0..1
    softness: 0.2, // soft edge falloff outside `width`
    sweepSeconds: 0.45, // one-shot travel duration ≈ 1 / 2.15 (the ported material's authored autoSpeed, sweeps per second)
    startDelaySeconds: 0.05, // begin as the symbol reaches its scale-up peak (scalePulse.scaleUpSeconds 0.1)
    easing: "linear", // constant-speed glint, like the filter's self-animating mode
  },

  // Overlay "switch" symbol-win animation (OverlayFadeSymbolWinEffect), in the sprite winner
  // composite when the symbols atlas carries the alt frames. This game runs it in HOLD mode
  // (createPresentation passes hold: true): the H symbols cross-fade to their alt frame (H1-2, …)
  // over fadeInSeconds and STAY on it until they tumble away — in addition to the scale-pulse
  // (which this game runs once per win). The default switch → hold → back mode (other games) also
  // needs holdSeconds + fadeOutSeconds (+ a switchBackEffect for a switch-back pulse).
  symbolFrames: {
    fadeInSeconds: 0.05,
    // Per-cell lead-in before the H-symbol switches base → alt frame (0 = immediate).
    // Same ms clock as the dim / brightness / scale-pulse delays above; sweeps with the symbols.
    startDelayMilliseconds: 100,
    // The OUTGOING base frame holds at full this long before fading out, so the INCOMING
    // overlay covers the cell first — no see-through during the switch. 0 = both cross-fade
    // together (dips to ~75% coverage mid-fade); = fadeInSeconds (0.2) = zero transparency,
    // fully sequential; between = partial overlap. Tune down for more cross-fade blend.
    baseFadeOutDelaySeconds: 0.015,
  },

  autoAdvance: {
    // Hold AFTER a round ends before the next AUTO-ADVANCED spin starts (free spins + base-game
    // autospin) — so the result registers instead of the next reveal cutting in instantly. Manual
    // base spins don't use either (they idle until the next tap). Applied in
    // the auto-advance-hold plugin at the spin end. CONSTANT perceived duration across speed modes
    // (time-scale compensated, but still engine-clock so it pauses / frame-steps).
    //
    // Two beats: a longer one on a WIN (linger on the win amount) and a short one on a NO-WIN round
    // (a small breath so an auto-advancing dead spin doesn't snap straight into the next). 0 = off.
    postWinHoldSeconds: 0.5,
    postRoundHoldSeconds: 0.25,
  },
  // Delay BEFORE presenting each win highlight in the first (awaited) pass: the FIRST
  // win of the spin waits firstSeconds (a beat after the reels land), each subsequent
  // win waits subsequentSeconds. Tune to taste.
  winHighlightDelay: {
    firstSeconds: 0.1,
    subsequentSeconds: 0.00,
  },
};
